import React, { useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getStatusColor, getCountryFlag } from '../utils/geo';
import { useTheme } from '../contexts/ThemeContext';

// ========== ANIMATED PACKET ==========
class AnimatedPacket {
  constructor(map, points, color, speed = 1, size = 8) {
    this.map = map;
    this.points = points;
    this.color = color;
    this.speed = speed;
    this.progress = Math.random();
    this.alive = true;

    this.icon = L.divIcon({
      className: 'packet-marker',
      html: `<div style="
        width: ${size}px; height: ${size}px;
        background: ${color};
        border-radius: 50%;
        box-shadow: 0 0 ${size * 2}px ${color}, 0 0 ${size * 4}px ${color}40;
        filter: brightness(1.5);
      "></div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });

    this.marker = L.marker([points[0][0], points[0][1]], {
      icon: this.icon,
      interactive: false,
      zIndexOffset: 1000,
    }).addTo(map);
  }

  update(delta) {
    if (!this.alive || this.points.length < 2) return;
    this.progress += delta * this.speed * 0.3;
    if (this.progress >= 1) this.progress = 0;

    const totalLen = this.points.length - 1;
    const exactIdx = this.progress * totalLen;
    const idx = Math.floor(exactIdx);
    const frac = exactIdx - idx;

    if (idx >= 0 && idx < this.points.length - 1) {
      const lat = this.points[idx][0] + (this.points[idx + 1][0] - this.points[idx][0]) * frac;
      const lng = this.points[idx][1] + (this.points[idx + 1][1] - this.points[idx][1]) * frac;
      this.marker.setLatLng([lat, lng]);
    }
  }

  destroy() {
    this.alive = false;
    if (this.map && this.marker) {
      try { this.map.removeLayer(this.marker); } catch(e) {}
    }
  }
}

function generateIntermediatePoints(start, end, numPoints) {
  const points = [];
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    points.push([
      start[0] + (end[0] - start[0]) * t,
      start[1] + (end[1] - start[1]) * t,
    ]);
  }
  return points;
}

// ========== MAIN MAP COMPONENT ==========
export default function MapScene({ hops, allHops, visibleHops, traceInfo, traceState, showTimeoutHops }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const { isDark } = useTheme();
  const layersRef = useRef({
    hopMarkers: [],
    routeLines: [],
    glowLines: [],
    packets: [],
    sourceMarker: null,
  });
  const animRef = useRef(null);
  const lastTimeRef = useRef(0);
  const cleanupTriggeredRef = useRef(false);

  // Dedicated cleanup function for previous trace
  const clearPreviousTrace = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const layers = layersRef.current;

    // Stop all animations
    layers.packets.forEach(p => p.destroy());
    
    // Remove all markers
    layers.hopMarkers.forEach(m => {
      try { map.removeLayer(m); } catch(e) {}
    });
    
    // Remove all lines
    layers.routeLines.forEach(l => {
      try { map.removeLayer(l); } catch(e) {}
    });
    layers.glowLines.forEach(l => {
      try { map.removeLayer(l); } catch(e) {}
    });
    
    // Remove source marker
    if (layers.sourceMarker) {
      try { map.removeLayer(layers.sourceMarker); } catch(e) {}
      layers.sourceMarker = null;
    }

    // Clear all layer arrays
    layers.hopMarkers = [];
    layers.routeLines = [];
    layers.glowLines = [];
    layers.packets = [];
    
    cleanupTriggeredRef.current = true;
  }, []);

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [30, 10],
      zoom: 3,
      zoomControl: false,
      attributionControl: false,
      maxBounds: [[-85, -200], [85, 200]],
      minZoom: 2,
      maxZoom: 12,
    });

    // Initial tile layer - only OpenStreetMap (no API key needed)
    const tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    
    tileLayerRef.current = L.tileLayer(tileUrl, {
      subdomains: 'abc',
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
      className: isDark ? 'map-tiles-dark' : 'map-tiles-light',
    }).addTo(map);

    L.control.zoom({ position: 'bottomright' }).addTo(map);
    L.control.attribution({ position: 'bottomleft', prefix: false })
      .addAttribution('&copy; <a href="https://carto.com/">CARTO</a>')
      .addTo(map);

    mapRef.current = map;

    const animate = (time) => {
      animRef.current = requestAnimationFrame(animate);
      const delta = Math.min((time - lastTimeRef.current) / 1000, 0.1);
      lastTimeRef.current = time;
      layersRef.current.packets.forEach(p => p.update(delta));
    };
    animRef.current = requestAnimationFrame(animate);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Clear previous trace when new trace starts
  useEffect(() => {
    if (traceState === 'connecting' || traceState === 'idle') {
      clearPreviousTrace();
      cleanupTriggeredRef.current = false;
    }
  }, [traceState, clearPreviousTrace]);

  // Update tile layer when theme changes
  useEffect(() => {
    if (!mapRef.current || !tileLayerRef.current) return;
    
    const map = mapRef.current;
    
    // Remove old tile layer
    map.removeLayer(tileLayerRef.current);
    
    // Add new tile layer with appropriate class
    tileLayerRef.current = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      subdomains: 'abc',
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
      className: isDark ? 'map-tiles-dark' : 'map-tiles-light',
    }).addTo(map);
  }, [isDark]);

  // Dynamic map fitting - update bounds as hops are added
  useEffect(() => {
    if (!mapRef.current || !traceInfo) return;
    
    const map = mapRef.current;
    const srcLat = traceInfo.source_lat;
    const srcLon = traceInfo.source_lon;
    const tgtLat = traceInfo.target_lat;
    const tgtLon = traceInfo.target_lon;

    // Initial view on trace start
    if (traceState === 'tracing' && hops.length === 0) {
      if (srcLat && srcLon && tgtLat && tgtLon) {
        map.fitBounds(
          L.latLngBounds([srcLat, srcLon], [tgtLat, tgtLon]).pad(0.3),
          { animate: true, duration: 1.5, maxZoom: 10 }
        );
      } else if (srcLat && srcLon) {
        map.setView([srcLat, srcLon], 4, { animate: true });
      }
    }

    // Dynamic fit as hops are added (every 3 hops or when complete)
    if (hops.length > 0 && (hops.length % 3 === 0 || traceState === 'complete')) {
      const bounds = L.latLngBounds([]);
      
      // Add source
      if (srcLat && srcLon && !(srcLat === 0 && srcLon === 0)) {
        bounds.extend([srcLat, srcLon]);
      }
      
      // Add all responding hops with valid coordinates
      hops.forEach(hop => {
        if (!hop.filtered && hop.lat && hop.lon && !(hop.lat === 0 && hop.lon === 0)) {
          bounds.extend([hop.lat, hop.lon]);
        }
      });
      
      // Add target
      if (tgtLat && tgtLon && !(tgtLat === 0 && tgtLon === 0)) {
        bounds.extend([tgtLat, tgtLon]);
      }

      if (bounds.isValid()) {
        // Adjust timing: faster during trace, slower on complete
        const delay = traceState === 'complete' ? 300 : 100;
        setTimeout(() => {
          map.fitBounds(bounds, {
            padding: [50, 50],
            maxZoom: 10,
            animate: true,
            duration: traceState === 'complete' ? 1.5 : 0.8
          });
        }, delay);
      }
    }
  }, [traceInfo, traceState, hops]);

  // Update hop visualization
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const layers = layersRef.current;

    // Always clear old layers first
    layers.hopMarkers.forEach(m => {
      try { map.removeLayer(m); } catch(e) {}
    });
    layers.routeLines.forEach(l => {
      try { map.removeLayer(l); } catch(e) {}
    });
    layers.glowLines.forEach(l => {
      try { map.removeLayer(l); } catch(e) {}
    });
    layers.packets.forEach(p => p.destroy());
    
    if (layers.sourceMarker) {
      try { map.removeLayer(layers.sourceMarker); } catch(e) {}
      layers.sourceMarker = null;
    }
    
    layers.hopMarkers = [];
    layers.routeLines = [];
    layers.glowLines = [];
    layers.packets = [];

    // If no trace info or empty hops, keep map clean
    if (!traceInfo || hops.length === 0) {
      return;
    }

    // Source marker
    const sourceIcon = L.divIcon({
      className: 'source-marker',
      html: `<div class="map-marker-pulse" style="--color: #00ff9d;">
        <div class="marker-dot" style="background: #00ff9d; width: 14px; height: 14px;"></div>
        <div class="marker-ring" style="border-color: #00ff9d;"></div>
      </div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });
    layers.sourceMarker = L.marker([traceInfo.source_lat, traceInfo.source_lon], { icon: sourceIcon, zIndexOffset: 500 })
      .addTo(map)
      .bindPopup(`
        <div class="cyber-popup">
          <div class="popup-title">Source</div>
          <div class="popup-row"><span class="popup-label">IP:</span> <span class="popup-value font-mono">${traceInfo.source_ip}</span></div>
          <div class="popup-row"><span class="popup-label">Location:</span> <span class="popup-value">${traceInfo.source_city || 'Unknown'}, ${traceInfo.source_country || ''}</span></div>
        </div>
      `, { className: 'cyber-popup-container' });

    // Track last known position for drawing lines
    let lastKnownLat = traceInfo.source_lat;
    let lastKnownLon = traceInfo.source_lon;

    hops.forEach((hop, idx) => {
      const isFiltered = hop.filtered || hop.status === 'filtered';
      const isTimeout = hop.isTimeout || false;

      // Skip timeout hops if not in showTimeoutHops mode
      // Also skip if no valid coordinates
      if (isFiltered || !hop.lat || !hop.lon || (hop.lat === 0 && hop.lon === 0)) {
        return;
      }

      const color = getStatusColor(hop.status);
      const flag = getCountryFlag(hop.country_code);
      const isLatest = idx === hops.length - 1 && traceState === 'tracing';
      const markerSize = isLatest ? 12 : 10;

      // Hop marker
      const hopIcon = L.divIcon({
        className: 'hop-marker',
        html: `<div class="map-marker-pulse" style="--color: ${color};">
          <div class="marker-dot" style="background: ${color}; width: ${markerSize}px; height: ${markerSize}px;"></div>
          ${isLatest ? '<div class="marker-ring" style="border-color: ' + color + ';"></div>' : ''}
        </div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });

      const marker = L.marker([hop.lat, hop.lon], { icon: hopIcon, zIndexOffset: 400 })
        .addTo(map)
        .bindPopup(`
          <div class="cyber-popup">
            <div class="popup-title">${flag} Hop ${hop.hop_number}</div>
            <div class="popup-row"><span class="popup-label">IP:</span> <span class="popup-value font-mono">${hop.ip}</span></div>
            <div class="popup-row"><span class="popup-label">ISP:</span> <span class="popup-value">${hop.isp}</span></div>
            <div class="popup-row"><span class="popup-label">City:</span> <span class="popup-value">${hop.city || 'Unknown'}, ${hop.country_code}</span></div>
            <div class="popup-row"><span class="popup-label">Latency:</span> <span class="popup-value" style="color:${color}">${hop.latency_ms != null ? hop.latency_ms + 'ms' : '--'}</span></div>
            <div class="popup-row"><span class="popup-label">Packet Loss:</span> <span class="popup-value" style="color:${hop.packet_loss > 0 ? '#ff3b3b' : '#00ff9d'}">${hop.packet_loss}%</span></div>
            ${hop.as_info ? '<div class="popup-row"><span class="popup-label">AS:</span> <span class="popup-value" style="font-size:9px">' + hop.as_info + '</span></div>' : ''}
            <div class="popup-status" style="background: ${color}20; color: ${color}; border: 1px solid ${color}40;">
              ${hop.status.toUpperCase()}
            </div>
          </div>
        `, { className: 'cyber-popup-container' });
      layers.hopMarkers.push(marker);

      // Route line from last known position
      const prevPoint = [lastKnownLat, lastKnownLon];
      const curPoint = [hop.lat, hop.lon];

      // Glow line
      const glowLine = L.polyline([prevPoint, curPoint], {
        color: color,
        weight: 6,
        opacity: 0.2,
        smoothFactor: 1,
        className: 'route-glow-line',
      }).addTo(map);
      layers.glowLines.push(glowLine);

      // Main route line
      const routeLine = L.polyline([prevPoint, curPoint], {
        color: color,
        weight: 2.5,
        opacity: 0.8,
        smoothFactor: 1,
      }).addTo(map);
      layers.routeLines.push(routeLine);

      // Animated packet
      if (traceState === 'complete' || traceState === 'tracing') {
        const segPoints = generateIntermediatePoints(prevPoint, curPoint, 20);
        const pkt = new AnimatedPacket(map, segPoints, color, 0.8 + Math.random() * 0.6, 7);
        layers.packets.push(pkt);
      }

      // Update last known position
      lastKnownLat = hop.lat;
      lastKnownLon = hop.lon;
    });

  }, [hops, traceInfo, traceState]);

  return (
    <div
      ref={containerRef}
      className="map-container"
      data-testid="globe-canvas"
      style={{ width: '100%', height: '100%', minHeight: '500px', borderRadius: '1rem', overflow: 'hidden' }}
    />
  );
}
